import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import AddIcon from '@mui/icons-material/Add'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Switch
} from '@mui/material'
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid'
import CatalogAPI from 'api/faber/CatalogAPI'
import NormalText from 'common/Typography/NormalText'
import OrbitSession from 'hooks/useCookies'

export interface ManageAddPageCatalogProps {
  pageId: string
  destinationName: string
  handleClose: () => void
}

interface NonMappedCatalogApiRow {
  id?: string
  catalogId?: string
  catalog_id?: string
  name?: string
  packageType?: string
  esimConfigId?: number
  provisionMethod?: string | { method?: string }
  dataInGB?: number
  validityInDays?: number
  voiceInMins?: number
}

interface NonMappedCatalogResponse {
  data?: NonMappedCatalogApiRow[] | { data?: NonMappedCatalogApiRow[] }
  body?: {
    data?: NonMappedCatalogApiRow[]
  }
}

interface CatalogTableRow {
  id: string
  catalogId: string
  name: string
  packageType: string
  esimConfigId: number
  provisionMethod: string
  dataInGB: number
  validityInDays: number
  voiceInMins: number
}

interface MapCatalogPayload {
  destinationName: string
  catalogId: string
  pageId: string
  visibility: boolean
}

export default function AddCatalogToPage({
  pageId,
  destinationName,
  handleClose
}: ManageAddPageCatalogProps) {
  const faberToken = OrbitSession.get('faberToken')

  const [rows, setRows] = useState<CatalogTableRow[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false)
  const [selectedRow, setSelectedRow] = useState<CatalogTableRow | null>(null)
  const [visibility, setVisibility] = useState(true)

  const shouldFetch = Boolean(faberToken && pageId)

  const normalizeRows = useMemo(
    () => (response: NonMappedCatalogResponse | undefined) => {
      const root = response || {}
      const body = (root.data || root.body || root) as
        | NonMappedCatalogApiRow[]
        | {
        data?: NonMappedCatalogApiRow[]
        catalogs?: NonMappedCatalogApiRow[]
        items?: NonMappedCatalogApiRow[]
        nonMapped?: NonMappedCatalogApiRow[]
      }

      const rawRows =
        (Array.isArray(body) ? body : body.data) ||
        (!Array.isArray(body) ? body.catalogs : undefined) ||
        (!Array.isArray(body) ? body.items : undefined) ||
        (!Array.isArray(body) ? body.nonMapped : undefined) ||
        []

      if (!Array.isArray(rawRows)) {
        return [] as CatalogTableRow[]
      }

      return rawRows
        .map((row) => {
          const catalogId = row.catalogId || row.catalog_id || row.id
          const id = row.id || catalogId
          if (!id || !catalogId) {
            return null
          }

          const provisionMethod =
            typeof row.provisionMethod === 'string'
              ? row.provisionMethod
              : row.provisionMethod?.method || ''

          return {
            id: String(id),
            catalogId: String(catalogId),
            name: row.name || '-',
            packageType: row.packageType || '-',
            esimConfigId: Number(row.esimConfigId || 0),
            provisionMethod: provisionMethod || '-',
            dataInGB: Number(row.dataInGB || 0),
            validityInDays: Number(row.validityInDays || 0),
            voiceInMins: Number(row.voiceInMins || 0)
          } as CatalogTableRow
        })
        .filter((row): row is CatalogTableRow => Boolean(row))
    },
    []
  )

  const fetchNonMappedCatalogs = useCallback(async () => {
    if (!shouldFetch) {
      setRows([])
      return
    }

    setIsLoading(true)
    try {
      const response = (await CatalogAPI.getNonMappedCatalogItems(
        faberToken
      )) as NonMappedCatalogResponse
      setRows(normalizeRows(response))
    } catch (error) {
      toast.error('Failed to fetch non-mapped catalog items')
      setRows([])
    } finally {
      setIsLoading(false)
    }
  }, [faberToken, normalizeRows, shouldFetch])

  useEffect(() => {
    void fetchNonMappedCatalogs()
  }, [fetchNonMappedCatalogs])

  const handleOpenConfirm = (row: CatalogTableRow) => {
    setSelectedRow(row)
    setVisibility(true)
    setOpenConfirmDialog(true)
  }

  const handleCloseConfirm = () => {
    if (isSubmitting) {
      return
    }
    setOpenConfirmDialog(false)
    setSelectedRow(null)
    setVisibility(true)
  }

  const handleConfirmMap = async () => {
    if (!selectedRow?.catalogId) {
      toast.error('Invalid catalog selected.')
      return
    }

    setIsSubmitting(true)
    const payload: MapCatalogPayload = {
      destinationName,
      catalogId: selectedRow.catalogId,
      pageId,
      visibility
    }

    try {
      await CatalogAPI.mapCatalogToDestination(faberToken, payload)
      toast.success('Catalog added successfully!')
      handleCloseConfirm()
      handleClose()
    } catch (error) {
      toast.error('Failed to add catalog!')
    } finally {
      setIsSubmitting(false)
    }
  }

  const columns = useMemo<GridColDef<CatalogTableRow>[]>(
    () => [
      { field: 'id', headerName: 'ID', minWidth: 220, flex: 1 },
      { field: 'catalogId', headerName: 'Catalog ID', minWidth: 220, flex: 1 },
      { field: 'name', headerName: 'Name', minWidth: 180, flex: 1 },
      { field: 'packageType', headerName: 'Package Type', minWidth: 140, flex: 1 },
      { field: 'esimConfigId', headerName: 'eSIM Config ID', minWidth: 180, flex: 1 },
      { field: 'provisionMethod', headerName: 'Provision Method', minWidth: 200, flex: 1 },
      { field: 'dataInGB', headerName: 'Data In GB', minWidth: 120, type: 'number', flex: 0.7 },
      { field: 'validityInDays', headerName: 'Validity In Days', minWidth: 140, type: 'number', flex: 0.8 },
      { field: 'voiceInMins', headerName: 'Voice In Mins', minWidth: 130, type: 'number', flex: 0.8 },
      {
        field: 'add',
        headerName: 'Add',
        sortable: false,
        filterable: false,
        minWidth: 90,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params: GridRenderCellParams<CatalogTableRow>) => (
          <IconButton
            color='primary'
            size='small'
            aria-label='add-catalog'
            onClick={(event) => {
              event.stopPropagation()
              const clickedRow = params?.row
              if (!clickedRow) {
                return
              }
              handleOpenConfirm(clickedRow)
            }}
          >
            <AddIcon fontSize='small' />
          </IconButton>
        )
      }
    ],
    []
  )

  return (
    <Box>
      <NormalText isBold>Catalog Items</NormalText>
      <Box sx={{ mt: 1, height: 640 }}>
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => row.id}
          loading={isLoading}
          disableSelectionOnClick
          pageSize={10}
          rowsPerPageOptions={[10, 20, 50]}
        />
      </Box>

      <Dialog
        open={openConfirmDialog}
        onClose={handleCloseConfirm}
        aria-labelledby='map-catalog-dialog-title'
        aria-describedby='map-catalog-dialog-description'
      >
        <DialogTitle id='map-catalog-dialog-title'>Confirm Add Catalog</DialogTitle>
        <DialogContent>
          <DialogContentText id='map-catalog-dialog-description'>
            Do you want to map this catalog to destination{' '}
            <strong>{destinationName}</strong>?
          </DialogContentText>
          <FormControlLabel
            sx={{ mt: 1 }}
            control={
              <Switch
                checked={visibility}
                onChange={(event) => setVisibility(event.target.checked)}
                disabled={isSubmitting}
              />
            }
            label='Visibility'
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirm} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            variant='contained'
            onClick={handleConfirmMap}
            disabled={isSubmitting || !selectedRow}
          >
            {isSubmitting ? 'Adding...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
